<script setup lang="ts">
/**
 * A text block, in two tabs.
 *
 * - **Format**: the words being written, from the selection on the page (see
 *   `TextFormatTools`): style, bold to subscript, size, colour, highlight,
 *   alignment, lists, quotation, link.
 * - **Layout**: the block as a whole (see `TextSetting` in `domain/prose.ts`):
 *   its columns, their gap and the rule between them, its alignment and
 *   hyphenation, its leading, a frame, a drop cap, and the named style the
 *   whole block wears.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import TextFormatTools from '@/components/builder/TextFormatTools.vue'
import { useRememberedTab } from '@/composables/useRememberedTab'
import {
  BLOCK_ALIGNS,
  COLUMN_GAPS,
  LEADINGS,
  TEXT_COLUMNS,
  TEXT_FRAMES,
  type TextSetting,
} from '@/domain/prose'
import type { TextSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'
import TextStyleField from '@/sections/TextStyleField.vue'

const { t } = useI18n()

const props = defineProps<{ section: TextSection }>()
const emit = defineEmits<{ change: [section: TextSection] }>()

type TextTab = 'format' | 'layout'

const tab = useRememberedTab<TextTab>('text', ['format', 'layout'])

const tabs = computed<InspectorTab<TextTab>[]>(() => [
  { id: 'format', label: t('inspector.text.tabs.format'), icon: TAB_ICONS.format },
  { id: 'layout', label: t('inspector.text.tabs.layout'), icon: TAB_ICONS.layout },
])

function set(changes: Partial<TextSection> | Partial<TextSetting>): void {
  emit('change', { ...props.section, ...changes })
}

function checked(event: Event): boolean {
  return (event.target as HTMLInputElement).checked
}
</script>

<template>
  <InspectorTabs v-model:open="tab" :tabs="tabs" :label="t('inspector.text.tabs.label')">
    <template #format>
      <TextFormatTools />
    </template>

    <template #layout>
      <div class="fields">
        <InspectorField
          :label="t('inspector.text.columns')"
          :hint="t('inspector.text.columnsHint')"
        >
          <div class="segments" role="radiogroup" :aria-label="t('inspector.text.columns')">
            <button
              v-for="count in TEXT_COLUMNS"
              :key="count"
              type="button"
              role="radio"
              class="segment"
              :class="{ 'segment--on': count === section.columns }"
              :aria-checked="count === section.columns"
              @click="set({ columns: count })"
            >
              <span class="lanes" aria-hidden="true">
                <span v-for="lane in count" :key="lane" class="lane" />
              </span>
              {{ t(`inspector.text.columnCounts.${count}`) }}
            </button>
          </div>
        </InspectorField>

        <template v-if="section.columns > 1">
          <InspectorField :label="t('inspector.text.layout.gap')">
            <div class="segments" role="radiogroup" :aria-label="t('inspector.text.layout.gap')">
              <button
                v-for="gap in COLUMN_GAPS"
                :key="gap"
                type="button"
                role="radio"
                class="segment"
                :class="{ 'segment--on': gap === section.columnGap }"
                :aria-checked="gap === section.columnGap"
                @click="set({ columnGap: gap })"
              >
                {{ t(`inspector.text.layout.gaps.${gap}`) }}
              </button>
            </div>
          </InspectorField>
          <label class="b-switch">
            <input
              type="checkbox"
              :checked="section.columnRule"
              @change="set({ columnRule: checked($event) })"
            />
            <span class="b-switch__track" />
            <span class="switch-label">{{ t('inspector.text.layout.rule') }}</span>
          </label>
        </template>

        <InspectorField :label="t('inspector.text.layout.align')">
          <div class="segments" role="radiogroup" :aria-label="t('inspector.text.layout.align')">
            <button
              v-for="align in BLOCK_ALIGNS"
              :key="align"
              type="button"
              role="radio"
              class="segment"
              :class="{ 'segment--on': align === section.align }"
              :aria-checked="align === section.align"
              @click="set({ align })"
            >
              {{ t(`inspector.text.layout.aligns.${align}`) }}
            </button>
          </div>
        </InspectorField>

        <InspectorField :label="t('inspector.text.layout.leading')">
          <div class="segments" role="radiogroup" :aria-label="t('inspector.text.layout.leading')">
            <button
              v-for="leading in LEADINGS"
              :key="leading"
              type="button"
              role="radio"
              class="segment"
              :class="{ 'segment--on': leading === section.leading }"
              :aria-checked="leading === section.leading"
              @click="set({ leading })"
            >
              {{ t(`inspector.text.layout.leadings.${leading}`) }}
            </button>
          </div>
        </InspectorField>

        <InspectorField
          :label="t('inspector.text.layout.frame')"
          :hint="t('inspector.text.layout.frameHint')"
        >
          <div class="frames" role="radiogroup" :aria-label="t('inspector.text.layout.frame')">
            <button
              v-for="frame in TEXT_FRAMES"
              :key="frame"
              type="button"
              role="radio"
              class="frame"
              :class="[`frame--${frame}`, { 'frame--on': frame === section.frame }]"
              :aria-checked="frame === section.frame"
              @click="set({ frame })"
            >
              <span class="frame__sample" aria-hidden="true"> <span /><span /><span /> </span>
              {{ t(`inspector.text.layout.frames.${frame}`) }}
            </button>
          </div>
        </InspectorField>

        <div class="switches">
          <label class="b-switch">
            <input
              type="checkbox"
              :checked="section.hyphens"
              @change="set({ hyphens: checked($event) })"
            />
            <span class="b-switch__track" />
            <span class="switch-label">{{ t('inspector.text.layout.hyphens') }}</span>
          </label>
          <label class="b-switch">
            <input
              type="checkbox"
              :checked="section.dropCap"
              @change="set({ dropCap: checked($event) })"
            />
            <span class="b-switch__track" />
            <span class="switch-label">{{ t('inspector.text.layout.dropCap') }}</span>
          </label>
        </div>

        <TextStyleField :value="section.textStyleId" @change="set({ textStyleId: $event })" />
      </div>
    </template>
  </InspectorTabs>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.switches {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.switch-label {
  font-size: var(--text-base);
}

.segments {
  display: flex;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  min-width: 0;
  height: 30px;
  padding: 0 var(--sp-2);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.lanes {
  display: inline-flex;
  gap: 2px;
}

.lane {
  width: 6px;
  height: 11px;
  border-radius: 1px;
  background: currentColor;
  opacity: 0.55;
}

/* Each frame shown as it would look: three lines of text in it. */
.frames {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--sp-2);
}

.frame {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--sp-2);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
}

.frame--on {
  border-color: var(--accent-fill);
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.frame__sample {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 34px;
  padding: 4px;
  border-radius: 3px;
  background: var(--panel);
}

.frame__sample span {
  height: 2px;
  border-radius: 1px;
  background: var(--ink-faint);
}

.frame__sample span:last-child {
  width: 60%;
}

.frame--tint .frame__sample {
  background: color-mix(in srgb, var(--ink) 9%, var(--panel));
}

.frame--outline .frame__sample {
  box-shadow: inset 0 0 0 1px var(--ink-faint);
}

.frame--accent .frame__sample {
  background: color-mix(in srgb, var(--accent-fill) 18%, var(--panel));
}
</style>
